import { useEffect, useState } from 'react';
import { AUTH_CONSTANTS } from '../_MODULES/_API';
import { useForm } from '../../../../../_CORE/APP/Templates/_UI/Form/_index';
import { useApiActionMutation } from '../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation';
import { useUserAuthenticated } from '../../../../../_CORE/APP/Hooks/Contexts/_Auth/userAuthenticatedContext';
import { REACT_HOT_TOAST_SUCCESS } from '../../../../../_CORE/HELPERS/reactHotToast.helper';

export const useAuthLogin = () => {
    const { onCallbackCredentialUserAuth } = useUserAuthenticated();
    const [verification, setVerification] = useState(null);
    const [resendIn, setResendIn] = useState(0);

    const loginAction = useApiActionMutation({ isToastError: true, minTimeMutate: 600 });
    const verifyAction = useApiActionMutation({ isToastError: true, minTimeMutate: 450 });
    const resendAction = useApiActionMutation({ isToastError: true, minTimeMutate: 350 });

    const VERIFY_FORM = useForm({
        initialValues: { code: '' },
        schema: {
            code: { required: true, pattern: { regex: /^\d{6}$/, message: 'Ingresá los 6 dígitos enviados por correo.' } },
        },
        onSubmit: async (values) => {
            if (!verification?.challengeId) return;

            const RESPONSE = await verifyAction.actionMutate({
                method: 'POST', endpoint: AUTH_CONSTANTS.VERIFY_LOGIN_CODE,
                data: { challengeId: verification.challengeId, code: String(values.code || '').trim() },
            });

            if (!RESPONSE.success || !RESPONSE.data?.accessToken) {
                VERIFY_FORM.applyApiError(RESPONSE);
                return;
            }

            await onCallbackCredentialUserAuth({ accessToken: RESPONSE.data.accessToken });
        },
    });

    const LOGIN_FORM = useForm({
        initialValues: { email: 'bailadorgaston1993@gmail.com', password: '' },
        schema: {
            email: { required: true, email: true, maxLength: 254 },
            password: { required: true, maxLength: 200 },
        },
        onSubmit: async (values) => {
            const RESPONSE = await loginAction.actionMutate({
                method: 'POST', endpoint: AUTH_CONSTANTS.LOGIN,
                data: { email: values.email.trim(), password: values.password },
            });

            if (!RESPONSE.success) {
                LOGIN_FORM.applyApiError(RESPONSE);
                return;
            }

            if (RESPONSE.data?.verificationRequired && RESPONSE.data?.challengeId) {
                VERIFY_FORM.reset({ code: '' });
                setVerification({
                    challengeId: RESPONSE.data.challengeId,
                    recipient: RESPONSE.data.recipient || 'tu correo de seguridad',
                    expiresInSeconds: Number(RESPONSE.data.expiresInSeconds || 600),
                });
                setResendIn(Number(RESPONSE.data.resendAvailableInSeconds || 60));
                return;
            }

            // Seguridad estricta: NOTEBOOK nunca acepta un JWT directo en el primer paso.
            // La sesión solamente se habilita después de /verify-login-code.
            LOGIN_FORM.applyApiError({
                message: 'El servidor no inició la verificación de seguridad obligatoria.',
            });
        },
    });

    useEffect(() => {
        if (resendIn <= 0) return undefined;
        const timer = window.setInterval(() => {
            setResendIn((current) => Math.max(0, current - 1));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [resendIn > 0]);

    const resendCode = async () => {
        if (!verification?.challengeId || resendIn > 0 || resendAction.useActionApiIsLoading) return;

        const RESPONSE = await resendAction.actionMutate({
            method: 'POST', endpoint: AUTH_CONSTANTS.RESEND_LOGIN_CODE,
            data: { challengeId: verification.challengeId },
        });

        if (!RESPONSE.success) return;

        VERIFY_FORM.reset({ code: '' });
        setVerification((current) => current ? {
            ...current,
            recipient: RESPONSE.data?.recipient || current.recipient,
            expiresInSeconds: Number(RESPONSE.data?.expiresInSeconds || 600),
        } : current);
        setResendIn(Number(RESPONSE.data?.resendAvailableInSeconds || 60));
        REACT_HOT_TOAST_SUCCESS('Te enviamos un código nuevo.');
    };

    const backToCredentials = () => {
        setVerification(null);
        setResendIn(0);
        VERIFY_FORM.reset({ code: '' });
        LOGIN_FORM.setValue('password', '');
        window.setTimeout(() => LOGIN_FORM.focusField('password'), 0);
    };

    return {
        LOGIN_FORM,
        VERIFY_FORM,
        verification,
        resendIn,
        resendCode,
        backToCredentials,
        isLoginLoading: loginAction.useActionApiIsLoading,
        isVerifyLoading: verifyAction.useActionApiIsLoading,
        isResendLoading: resendAction.useActionApiIsLoading,
    };
};
